import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  Toolbar, 
  ToolbarButton
} from '@mui/x-data-grid';
import { 
  useLazyFetchClubUsersQuery, 
  useAddClubUsersMutation, 
  useRmvClubUsersMutation, 
  useUpdateClubUsersMutation 
} from "../../store/club/ClubApi";
import { Paper, Stack, Typography } from '@mui/material';


/*
 * Enthält das '+' damit neue Einträge in das Modell
 * eingetragen werden kann. Hiermit lassen sich neue
 * Benutzer einfach dem Club zuordnen. 
 */
function EditToolbar(props) {
  const { setRows, setRowModesModel, year, club } = props;

  const handleClick = () => {
    const id =  Math.floor(Math.random() * (128000 - 50) + 50);
    setRows((oldRows) => [
      ...oldRows,
      { 
        id: id, 
        email: '', 
        first_name: '', 
        last_name: '', 
        mobile: '', 
        role: 'contact',
        responsible_for: 0,        
        isNew: true,
        year: year, 
        clubId: club.id,         
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'email' },
    }));
  };

  return (    
    <Toolbar position="fixed" >      
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "stretch",
          width: '100%',
        }}
      >
        <Typography 
          variant="h5"  
          justifyContent="left"  
          sx={{ flexGrow: 1 }}
        >Zugeordnete Kontakte</Typography>
        <Tooltip title="Betreuer hinzufügen">
          <ToolbarButton onClick={handleClick}>
            <AddIcon fontSize="small" />
          </ToolbarButton>
        </Tooltip>
      </Stack>
    </Toolbar>
  );
}

export function ClubUsers({year, club}) {
    
  const [rows, setRows] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [fetchClubUsers] = useLazyFetchClubUsersQuery();
  const [addClubUsers] = useAddClubUsersMutation();
  const [updateClubUsers] = useUpdateClubUsersMutation();
  const [rmvClubUsers] = useRmvClubUsersMutation();

  React.useEffect(() => {
    fetchClubUsers({
      year: year, 
      id: club.id
    })
    .unwrap()
    .then(
      data => setRows(
        data.map(({user, tournament, club, role, responsible_for}) => 
          {
            return {
              id              : user.id,
              email           : user.email,  
              first_name      : user.first_name,
              last_name       : user.last_name, 
              mobile          : user.mobile,
              role            : role,
              responsible_for : responsible_for,
              year            : tournament.year,
              clubId          : club.id,
              isNew           : false,
            }
          }
        )
      )
    )  
    .catch((rejected) => console.info(rejected));
  }, [year, club.id, fetchClubUsers]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  
  /*
   * Löscht den übergebenen Eintrag aus der Liste heraus. 
  */
  const handleDeleteClick = (id) => () => 
  {
    // Den Zielwert ermitteln
    const toDelete = rows.reduce(
      (rowToDelete, row) => {
        if (row.id === id)
        {
          return { 
            year  : row.year, 
            clubId: row.clubId, 
            userId: row.id,
          }
        }
        else
        {
          return rowToDelete;
        }
      }
      , { 
        year: 0, 
        clubId: 0, 
        userId: 0,
      }
    ); 

    // nun löschen wir den Eintrag über die Schnittstelle
    rmvClubUsers(toDelete)
    .unwrap()
    .then( 
      // Am Ende löschen wir noch die Einträge aus dem Filter
      () => setRows(rows.filter((row) => row.id !== id))          
    )
    .catch(
      (error) => console.error('Fehler beim Löschen ', error)
    );

  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) 
    {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  // Behandelt die Aktualisierung einer neuen Zeile. Wir schicken in diesem
  // Fall die Änderungen direkt an die Datenbank.
  const processRowUpdate = async (newRow) => {
    let updatedRow = { 
      ...newRow, 
      isNew: false,  
    };
    
    // Zwischen den unterschiedlichen Möglichkeiten unterscheiden - neue Zeile,
    // geänderte Zeile oder gelöschte Zeile...
    if (newRow.isNew)
    {
      // Wir haben also auf jeden Fall einen neuen Eintrag - den schicken wir jetzt
      // Weg und warten auf die Antwort. Diese Antwort enthält dann den kompletten
      // neuen Eintrag, den wir dann als neue Zeile einfügen...
      try 
      {
        updatedRow = await addClubUsers(updatedRow)
        .unwrap()
        .then(
          (payload) => 
          {
            return {
              id              : payload.user.id,
              email           : payload.user.email,  
              first_name      : payload.user.first_name,
              last_name       : payload.user.last_name, 
              mobile          : payload.user.mobile,
              role            : payload.role,
              responsible_for : payload.responsible_for,
              year            : payload.tournament.year,
              clubId          : payload.club.id,
              isNew           : false,
            }
          }
        );
      } 
      catch (err) 
      {
        console.error('Failed to save the post: ', err)
      }      
    }
    else 
    {
      try 
      {
        updatedRow = await updateClubUsers(updatedRow)
        .unwrap()
        .then(
          (payload) => 
          {
            return {
              id              : payload.user.id,
              email           : payload.user.email,  
              first_name      : payload.user.first_name,
              last_name       : payload.user.last_name, 
              mobile          : payload.user.mobile,
              role            : payload.role,
              responsible_for : payload.responsible_for,
              year            : payload.tournament.year,
              clubId          : payload.club.id,
              isNew           : false,
            }
          }
        );
      } 
      catch (err) 
      {
        console.error('Failed to save the post: ', err)
      }      
    }
    
    // Nun aktualisieren wir die eigentliche Liste wieder, damit die 
    // Darstellung wieder passt.
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };


  const columns = 
  [
    { 
      field: 'email',
      headerName: 'E-Mail',
      width: 180, 
      editable: true,
      flex: 1,
      // valueGetter: (value, row) => row.user.email 
    },
    {
      field: 'first_name',
      headerName: 'Vorname',
      width: 120,
      align: 'left',
      headerAlign: 'left',
      editable: true,
      flex: 1,
      // valueGetter: (value, row) => row.user.first_name
    },
    {
      field: 'last_name',
      headerName: 'Nachname',
      width: 180,
      editable: true,
      flex: 1,
      // valueGetter: (value, row) => row.user.last_name
    },
    {
      field: 'mobile',
      headerName: 'Telefon',
      width: 180,
      editable: true,
      flex: 1,
      // valueGetter: (value, row) => row.user.mobile
    },
    {
      field: 'role',
      headerName: 'Rolle',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: 
      [
        { value: 'contact', label: 'Vor-Ort AnsprechpartnerIn' },
        { value: 'supervisor', label: 'BetreuerIn' },
        { value: 'responsible', label: 'TrainerIn' }
      ]
    },
    {
      field: 'responsible_for',      
      headerName: 'Verantwortlich für',
      width: 120,
      editable: true, 
      type: 'singleSelect',
      valueOptions: 
      [
        { value:  0, label: 'Alle Teams' },
        { value: 10, label: 'weibliche Teams' },
        { value: 11, label: 'Teams wU8' },
        { value: 12, label: 'Teams wU10' },
        { value: 20, label: 'männliche Teams' },
        { value: 21, label: 'Teams mU8' },
        { value: 22, label: 'Teams mU10' },
        { value: 1, label: 'Altergruppe U8' },
        { value: 2, label: 'Altersgruppe U10' },
      ]
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Bearbeitung',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              material={{
                sx: {
                  color: 'primary.main',
                },
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];


  return(
    <Paper>
      <Box
        sx={{
          height: 300,
          width: '100%',
          '& .actions': {
            color: 'text.secondary',
          },
          '& .textPrimary': {
            color: 'text.primary',
          },
        }}
      >
        <DataGrid
          getRowId={ (row) => row.id } 
          rows={rows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          slots={{ toolbar: EditToolbar }}
          slotProps={{
            toolbar: { setRows, setRowModesModel, year, club },
          }}
          label="Verantwortliche"
          showToolbar
          hideFooter
        />
      </Box>
    </Paper>
  );
}

